import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import MultiImageUpload from "@/components/shared/MultiImageUpload";
import { UploadFolder } from "@/services/filesService";
import { channelProductsService } from "@/services/channelProductsService";
import { useStorefrontCategories } from "@/hooks/queries";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";

const AdminChannelProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);
  const productId = id ? Number(id) : 0;

  const { data: categories = [], isLoading: categoriesLoading } = useStorefrontCategories();

  const { data: existingProduct, isLoading: productLoading } = useQuery({
    queryKey: ["admin", "channel-product", productId],
    queryFn: () => channelProductsService.getById(productId),
    enabled: isEdit && productId > 0,
  });

  // Form states
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [categoryId, setCategoryId] = useState<string>("none");
  const [brand, setBrand] = useState("");
  const [unit, setUnit] = useState("piece");
  const [unitValue, setUnitValue] = useState("1");
  const [isVeg, setIsVeg] = useState(true);
  const [tags, setTags] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");

  // Pricing states
  const [mrp, setMrp] = useState("");
  const [distributorPrice, setDistributorPrice] = useState("");
  const [wholesalePrice, setWholesalePrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [deliveryCharge, setDeliveryCharge] = useState("0");
  const [gstPercent, setGstPercent] = useState("5");
  const [businessVolume, setBusinessVolume] = useState("4");
  const [distributorCommissionPercent, setDistributorCommissionPercent] = useState("5");
  const [wholesaleCommissionPercent, setWholesaleCommissionPercent] = useState("8");
  const [retailCommissionPercent, setRetailCommissionPercent] = useState("10");

  // Stock and catalogue visibility
  const [initialStock, setInitialStock] = useState("100");
  const [publish, setPublish] = useState(true);
  const [images, setImages] = useState<string[]>([]);

  // When editing, populate form fields
  useEffect(() => {
    if (existingProduct) {
      setName(existingProduct.name || "");
      setSku(existingProduct.sku || "");
      setBarcode(existingProduct.barcode || "");
      setCategoryId(existingProduct.categoryId ? String(existingProduct.categoryId) : "none");
      setBrand(existingProduct.brand || "");
      setUnit(existingProduct.unit || "piece");
      setUnitValue(String(existingProduct.unitValue || 1));
      setIsVeg(existingProduct.isVeg ?? true);
      setTags(existingProduct.tags || "");
      setShortDescription(existingProduct.shortDescription || "");
      setDescription(existingProduct.description || "");

      setMrp(String(existingProduct.mrp || ""));
      setDistributorPrice(String(existingProduct.distributorPrice || ""));
      setWholesalePrice(String(existingProduct.wholesalePrice || ""));
      setRetailPrice(String(existingProduct.retailPrice || ""));
      setSellingPrice(String(existingProduct.sellingPrice || ""));
      setDeliveryCharge(String(existingProduct.deliveryCharge || 0));
      setGstPercent(String(existingProduct.gstPercent || 0));
      setBusinessVolume(String(existingProduct.businessVolume || 0));
      setDistributorCommissionPercent(String(existingProduct.distributorCommissionPercent || 0));
      setWholesaleCommissionPercent(String(existingProduct.wholesaleCommissionPercent || 0));
      setRetailCommissionPercent(String(existingProduct.retailCommissionPercent || 0));

      setPublish(existingProduct.isActive);
      setImages(existingProduct.images || []);
    }
  }, [existingProduct]);

  // Quick autofill pricing ladder when MRP is entered (only if fields are currently empty)
  const handleMrpBlur = () => {
    const val = Number(mrp);
    if (!val || val <= 0) return;

    if (!sellingPrice) setSellingPrice(String(Math.round(val * 0.9)));
    if (!distributorPrice) setDistributorPrice(String(Math.round(val * 0.7)));
    if (!wholesalePrice) setWholesalePrice(String(Math.round(val * 0.78)));
    if (!retailPrice) setRetailPrice(String(Math.round(val * 0.85)));
  };

  const createMutation = useMutation({
    mutationFn: () =>
      channelProductsService.create({
        name: name.trim(),
        sku: sku.trim() || undefined,
        barcode: barcode.trim() || undefined,
        categoryId: categoryId !== "none" ? Number(categoryId) : undefined,
        description: description.trim() || undefined,
        shortDescription: shortDescription.trim() || undefined,
        mrp: Number(mrp) || 0,
        distributorPrice: Number(distributorPrice) || 0,
        wholesalePrice: Number(wholesalePrice) || 0,
        retailPrice: Number(retailPrice) || 0,
        sellingPrice: Number(sellingPrice) || Number(mrp) || 0,
        deliveryCharge: Number(deliveryCharge) || 0,
        gstPercent: Number(gstPercent) || 0,
        distributorCommissionPercent: Number(distributorCommissionPercent) || 0,
        wholesaleCommissionPercent: Number(wholesaleCommissionPercent) || 0,
        retailCommissionPercent: Number(retailCommissionPercent) || 0,
        businessVolume: Number(businessVolume) || 0,
        unit: unit.trim() || "piece",
        unitValue: Number(unitValue) || 1,
        isVeg,
        tags: tags.trim() || undefined,
        brand: brand.trim() || undefined,
        weight: 0,
        publish,
        initialStock: Math.max(0, Number(initialStock) || 0),
        images,
      }),
    onSuccess: (data) => {
      toast({
        title: "Channel product created",
        description: `"${data.name}" added to admin channel catalogue.`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "channel-products"] });
      navigate("/admin/channel-products");
    },
    onError: (error) => {
      toast({
        title: "Could not create channel product",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      channelProductsService.update(productId, {
        name: name.trim(),
        sku: sku.trim() || undefined,
        barcode: barcode.trim() || undefined,
        categoryId: categoryId !== "none" ? Number(categoryId) : undefined,
        description: description.trim() || undefined,
        shortDescription: shortDescription.trim() || undefined,
        mrp: Number(mrp) || 0,
        distributorPrice: Number(distributorPrice) || 0,
        wholesalePrice: Number(wholesalePrice) || 0,
        retailPrice: Number(retailPrice) || 0,
        sellingPrice: Number(sellingPrice) || Number(mrp) || 0,
        deliveryCharge: Number(deliveryCharge) || 0,
        gstPercent: Number(gstPercent) || 0,
        distributorCommissionPercent: Number(distributorCommissionPercent) || 0,
        wholesaleCommissionPercent: Number(wholesaleCommissionPercent) || 0,
        retailCommissionPercent: Number(retailCommissionPercent) || 0,
        businessVolume: Number(businessVolume) || 0,
        unit: unit.trim() || "piece",
        unitValue: Number(unitValue) || 1,
        isActive: publish,
        isVeg,
        tags: tags.trim() || undefined,
        brand: brand.trim() || undefined,
        weight: 0,
        images,
      }),
    onSuccess: (data) => {
      toast({
        title: "Channel product updated",
        description: `Pricing and details saved for "${data.name}".`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "channel-products"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "channel-product", productId] });
      navigate("/admin/channel-products");
    },
    onError: (error) => {
      toast({
        title: "Could not update channel product",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (!mrp || Number(mrp) <= 0) {
      toast({ title: "Valid MRP is required", variant: "destructive" });
      return;
    }

    if (isEdit) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isLoading = isEdit && productLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/channel-products">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight">
                {isEdit ? "Edit Channel Product" : "Add Channel Product"}
              </h1>
              <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
                <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Admin Owned
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Directly owned by admin for the distributor, wholesaler, and retailer network.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" asChild disabled={isPending}>
            <Link to="/admin/channel-products">Cancel</Link>
          </Button>
          <Button type="submit" disabled={isPending || !name.trim() || !mrp}>
            {isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Channel Product"}
          </Button>
        </div>
      </div>

      {/* Ownership Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex items-center gap-3 py-3">
          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
          <div className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Admin Ownership Notice:</span> This
            channel product belongs strictly to the authenticated Admin account. It has no
            relationship to any store, and requires no store BV allocation.
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Product Identity</CardTitle>
          <CardDescription>
            Core identifying details shown to channel partners and catalog buyers.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="product-name">Product name *</Label>
            <Input
              id="product-name"
              placeholder="e.g. Master Atta 10kg Premium"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-sku">SKU (Stock Keeping Unit)</Label>
            <Input
              id="product-sku"
              placeholder="e.g. CH-ATTA-10KG"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-barcode">Barcode / EAN</Label>
            <Input
              id="product-barcode"
              placeholder="Optional barcode number"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-category">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId} disabled={categoriesLoading}>
              <SelectTrigger id="product-category">
                <SelectValue placeholder="Select a category (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (General / Direct)</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-brand">Brand</Label>
            <Input
              id="product-brand"
              placeholder="e.g. Bihar Mall Select"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-unit">Unit type</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger id="product-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="piece">Piece</SelectItem>
                <SelectItem value="kg">Kilogram (kg)</SelectItem>
                <SelectItem value="gram">Gram (g)</SelectItem>
                <SelectItem value="litre">Litre (L)</SelectItem>
                <SelectItem value="ml">Millilitre (ml)</SelectItem>
                <SelectItem value="pack">Pack / Bundle</SelectItem>
                <SelectItem value="box">Box / Carton</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-unit-value">Unit value</Label>
            <Input
              id="product-unit-value"
              inputMode="numeric"
              placeholder="1"
              value={unitValue}
              onChange={(e) => setUnitValue(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3 sm:col-span-2">
            <div>
              <Label htmlFor="product-is-veg">Vegetarian product</Label>
              <p className="text-xs text-muted-foreground">Displays green veg mark in catalogue</p>
            </div>
            <Switch id="product-is-veg" checked={isVeg} onCheckedChange={setIsVeg} />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="product-short-desc">Short summary</Label>
            <Input
              id="product-short-desc"
              placeholder="Brief 1-line headline"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="product-desc">Full description</Label>
            <Textarea
              id="product-desc"
              rows={3}
              placeholder="Detailed specifications, storage instructions, etc."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="product-tags">Search tags</Label>
            <Input
              id="product-tags"
              placeholder="atta, wheat, flour, grain (comma separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing Ladder & Commissions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Distribution Pricing Ladder</CardTitle>
            <Badge variant="secondary" className="text-xs">
              <Sparkles className="mr-1 h-3 w-3" /> Multi-tier
            </Badge>
          </div>
          <CardDescription>
            Admin sells to Distributor (DP), Distributor sells to Wholesaler (WP), Wholesaler sells to
            Retailer (RP), Retailer bills Customer (CP).
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="price-mrp">MRP (₹) *</Label>
            <Input
              id="price-mrp"
              inputMode="numeric"
              placeholder="100"
              value={mrp}
              onChange={(e) => setMrp(e.target.value)}
              onBlur={handleMrpBlur}
              required
            />
            <p className="text-[11px] text-muted-foreground">Maximum Retail Price</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-dp">Distributor Price (DP ₹)</Label>
            <Input
              id="price-dp"
              inputMode="numeric"
              placeholder="60"
              value={distributorPrice}
              onChange={(e) => setDistributorPrice(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">Price distributor pays Admin</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-wp">Wholesale Price (WP ₹)</Label>
            <Input
              id="price-wp"
              inputMode="numeric"
              placeholder="70"
              value={wholesalePrice}
              onChange={(e) => setWholesalePrice(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">Price wholesaler pays Distributor</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-rp">Retail Price (RP ₹)</Label>
            <Input
              id="price-rp"
              inputMode="numeric"
              placeholder="75"
              value={retailPrice}
              onChange={(e) => setRetailPrice(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">Price retailer pays Wholesaler</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-cp">Customer Price (CP ₹) *</Label>
            <Input
              id="price-cp"
              inputMode="numeric"
              placeholder="80"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              required
            />
            <p className="text-[11px] text-muted-foreground">Selling price to customer</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-gst">GST (%)</Label>
            <Input
              id="price-gst"
              inputMode="numeric"
              placeholder="5"
              value={gstPercent}
              onChange={(e) => setGstPercent(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-bv">Business Volume (BV)</Label>
            <Input
              id="price-bv"
              inputMode="numeric"
              placeholder="4"
              value={businessVolume}
              onChange={(e) => setBusinessVolume(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="price-dc">Delivery charge (₹)</Label>
            <Input
              id="price-dc"
              inputMode="numeric"
              placeholder="0"
              value={deliveryCharge}
              onChange={(e) => setDeliveryCharge(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2 lg:col-span-4 border-t pt-4">
            <h4 className="text-sm font-semibold mb-2">Channel Partner Commission Rates (%)</h4>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="comm-dist">Distributor Commission %</Label>
                <Input
                  id="comm-dist"
                  inputMode="numeric"
                  placeholder="5"
                  value={distributorCommissionPercent}
                  onChange={(e) => setDistributorCommissionPercent(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">Credited on wholesale transfer</p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="comm-whole">Wholesaler Commission %</Label>
                <Input
                  id="comm-whole"
                  inputMode="numeric"
                  placeholder="8"
                  value={wholesaleCommissionPercent}
                  onChange={(e) => setWholesaleCommissionPercent(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">Credited on retailer supply</p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="comm-ret">Retailer Commission %</Label>
                <Input
                  id="comm-ret"
                  inputMode="numeric"
                  placeholder="10"
                  value={retailCommissionPercent}
                  onChange={(e) => setRetailCommissionPercent(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">Credited on customer order</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stock & Catalogue Visibility */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stock & Publishing</CardTitle>
          <CardDescription>
            Control the initial channel stock in Admin pool and catalog visibility.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="initial-stock">Initial Admin Stock Pool</Label>
              <Input
                id="initial-stock"
                inputMode="numeric"
                placeholder="100"
                value={initialStock}
                onChange={(e) => setInitialStock(e.target.value.replace(/\D/g, ""))}
              />
              <p className="text-xs text-muted-foreground">
                Starting units available in Admin inventory for distributors to order.
              </p>
            </div>
          )}

          <div
            className={`flex items-center justify-between rounded-lg border p-3 ${
              !isEdit ? "sm:mt-6" : "sm:col-span-2"
            }`}
          >
            <div>
              <Label htmlFor="publish-status">Publish to Channel Catalogue</Label>
              <p className="text-xs text-muted-foreground">
                Visible for distributors and wholesalers to order immediately
              </p>
            </div>
            <Switch id="publish-status" checked={publish} onCheckedChange={setPublish} />
          </div>
        </CardContent>
      </Card>

      {/* Product Images */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Product Images</CardTitle>
          <CardDescription>Upload quality images of the channel product.</CardDescription>
        </CardHeader>
        <CardContent>
          <MultiImageUpload
            value={images}
            onChange={setImages}
            folder={UploadFolder.Products}
            maxImages={5}
            disabled={isPending}
          />
        </CardContent>
      </Card>

      {/* Bottom Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button variant="outline" asChild disabled={isPending}>
          <Link to="/admin/channel-products">Cancel</Link>
        </Button>
        <Button type="submit" disabled={isPending || !name.trim() || !mrp}>
          {isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Channel Product"}
        </Button>
      </div>
    </form>
  );
};

export default AdminChannelProductForm;
